import { Box, FormControl, TextField, TextFieldProps } from '@mui/material';
import React from 'react';
import { useEffect, useState } from 'react';
import './Input.scss';
import { isHTMLTagsPresent, loadStateSession } from '../flyfishutils/utils';
import styled from 'styled-components';
type Props = {
	value?: string | undefined;
	label?: string;
	error?: boolean;
	errorText?: string | React.ReactNode;
	helperText?: string | React.ReactNode;
	fullWidth?: boolean;
	limit?: number;
	startIcon?: React.ReactNode;
	endIcon?: React.ReactNode;
	onChange?: Function;
	disAllowSpecialChar?: boolean;
	className?: string;
	ref?;
	initialRows?: number;
	readOnly?;
	imagePreview?: string;
} & TextFieldProps;

const INPUT = styled.div`
	& > .ff-input {
		&.MuiFormControl-root {
			width: 100%;
			min-width: 256px;
			max-width: 256px;
			margin: 0;
			padding: 5px 0 0;
			&.MuiFormHelperText-root {
				font-family: var(--fontFamily);
				font-size: 12px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: 1.5;
				letter-spacing: 0.4px;
				text-align: left;
			}
		}

		&.fullWidth {
			max-width: none;
		}

		.MuiFormLabel-root {
			transform: translate(12px, 13px) scale(1.25);
			font-family: var(--fontFamily);

			font-size: 12px;
			font-weight: normal;
			font-stretch: normal;
			font-style: normal;
			line-height: 1.5;
			letter-spacing: 0.4px;
			text-align: left;
			color: var(--black-60);

			&.MuiFormLabel-filled,
			&.Mui-focused {
				transform: translate(14px, -9px) scale(1);
			}

			&:not(.Mui-error).Mui-focused {
				color: var(--primary-color-ff-primary500);
			}

			&.Mui-focused.Mui-error,
			&.MuiFormLabel-filled.Mui-error,
			&.Mui-error {
				color: var(--vibrant-pink-400);
			}

			&:not(.Mui-focused) {
				color: var(--black-60);
			}

			&.Mui-disabled {
				pointer-events: none;
			}
		}

		.MuiInputBase-root {
			padding: 12px;
			border-radius: 8px;
			line-height: 1.5;
			color: var(--black);

			&:not(.Mui-focused, .Mui-disabled, .Mui-error):hover {
				.MuiOutlinedInput-notchedOutline {
					border-color: ${loadStateSession('colorMode') === 'dark'
						? 'var(--primary-color-ff-primary500)'
						: 'var(--colorScale-2)'};
				}
			}

			&:not(.Mui-disabled, .Mui-error).Mui-focused {
				.MuiOutlinedInput-notchedOutline {
					border-color: ${loadStateSession('colorMode') === 'dark'
						? 'var(--primary-color-ff-primary500)'
						: 'var(--colorScale-2)'};
				}
			}

			&.Mui-error {
				.MuiOutlinedInput-notchedOutline {
					border-color: var(--vibrant-pink-400);
				}
			}

			&.Mui-disabled {
				cursor: not-allowed;

				.MuiInputLabel-root,
				input,
				textarea {
					pointer-events: none;
					color: var(--black);
					-webkit-text-fill-color: var(--black);
					&.textarea {
						-webkit-text-fill-color: #bfbfbf;
					}
				}
			}

			.MuiOutlinedInput-notchedOutline {
				border-color: var(--black-20);
			}

			input,
			textarea {
				padding: 0;
				font-family: var(--fontFamily);

				font-size: 16px;
				font-weight: normal;
				font-stretch: normal;
				font-style: normal;
				line-height: 1.5;
				letter-spacing: 0.32px;
				color: var(--black);

				/* Change Autocomplete styles in Chrome*/
				&:-webkit-autofill,
				&:-webkit-autofill:hover,
				&:-webkit-autofill:focus {
					-webkit-text-fill-color: inherit;
					// -webkit-box-shadow: 0 0 0px 1000px var(--white) inset;
					transition: background-color 5000s ease-in-out 0s;
				}

				&::placeholder {
					color: var(--black-60);
					opacity: 1;
				}
			}
		}

		&.small {
			.MuiInputBase-root {
				padding: 7px 12px;
				gap: 12px;
			}
		}

		.MuiFormHelperText-root {
			font-size: 12px;
			letter-spacing: 0.4px;
			color: var(--black-60);
			margin: 0 12px;

			.error-container {
				display: flex;
				gap: 32px;
				justify-content: space-between;
				align-items: center;
				margin-top: 2px;
				& .error-text {
					font-family: var(--fontFamily);
					font-size: 12px;
					font-weight: normal;
					font-stretch: normal;
					font-style: normal;
					line-height: 1.5;
					letter-spacing: 0.4px;
					color: #c92929;
				}
			}
		}
	}
`;

const Input = ({
	value,
	label,
	helperText,
	errorText,
	error = false,
	fullWidth = false,
	limit,
	startIcon,
	endIcon,
	onChange,
	disAllowSpecialChar = false,
	className,
	ref,
	initialRows,
	imagePreview,
	readOnly = false,

	...rest
}: Props) => {
	const handleKeyUp = (
		event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		if (event.key === 'Escape') {
			// @ts-ignore
			event.target.blur();
		}
	};

	const [text, setText] = useState(helperText);
	const [limitError, setLimitError] = useState(false);
	const [inputError, setInputError] = useState('');
	useEffect(() => {
		if (limit && value && value?.length > limit) {
			setText('Character limit exceeded');
		} else {
			if (error && errorText && errorText) {
				setText(errorText);
			} else {
				setText(helperText);
			}
		}
	}, [error, limitError]);

	const handleChange = (
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
	) => {
		onChange && onChange(event);
		if (disAllowSpecialChar) {
			if (isHTMLTagsPresent(event.target.value)) {
				if (!disAllowSpecialChar) {
					setInputError('');
				} else {
					setInputError('Invalid Input');
				}
			} else {
				setInputError('');
			}
		}

		if (limit) {
			if (event.target.value.length > limit) {
				setLimitError(true);
			} else {
				setLimitError(false);
			}
		}
	};

	return (
		<INPUT className={className} ref={ref}>
			<FormControl
				variant='outlined'
				className={`ff-input${fullWidth ? ' fullWidth' : ''}${
					label ? '' : ' small'
				}`}>
				<TextField
					variant='outlined'
					label={label}
					value={value}
					onChange={handleChange}
					error={error || limitError || Boolean(inputError.length)}
					onFocus={(event) => event.target.select()}
					inputProps={{
						enterKeyHint: 'enter',
					}}
					InputProps={{
						startAdornment: startIcon,
						endAdornment: endIcon,
						onKeyUp: (event) => handleKeyUp(event),
						readOnly: readOnly,
					}}
					helperText={
						(text && (
							<Box component={'span'} className='error-container'>
								<span className='error-text'>{text}</span>
								{limit && limitError && (
									<span>
										{value?.length}/{limit}
									</span>
								)}
							</Box>
						)) ||
						(inputError && Boolean(inputError.length) && (
							<Box component={'span'} className='error-container'>
								<span
									className='error-text'
									style={{ color: 'var(--error-red)' }}>
									{inputError}
								</span>
							</Box>
						))
					}
					minRows={initialRows}
					maxRows={3}
					{...rest}
				/>
				{/* Image Preview Section */}
				{imagePreview && (
					<Box
						mt={2}
						sx={{
							position: 'absolute !important',
							left: '112px',
							marginTop: '6px',
						}}>
						<img
							src={imagePreview}
							alt='Image Preview'
							style={{
								width: '42px',
								aspectRatio: '1',
								borderRadius: '8px',
								boxShadow:
									'0px 0px 0px 1px #FFF, 0px 0px 0px 2px rgba(0, 0, 0, 0.25)',
								border: '1px solid rgba(0, 0, 0, 0.10)',
							}}
						/>
					</Box>
				)}
			</FormControl>
		</INPUT>
	);
};

export default Input;
